// Centralized eligibility/tiering engine for Forms A & B.
//
// Replaces the old pattern where each step component computed its own
// "CAAS/GLP1 indicated/contraindicated" verdict (spread across 6+ files, easy to get
// wrong or forget to act on — e.g. the old allergy screen computed a reject verdict
// and then never checked it before advancing). Every step now just saves its answer
// and calls checkImmediateRed() with the updated formData; only a RED result blocks
// the patient in the UI. Amber/Orange are accumulated and sent to the backend for the
// doctor to review — never enforced as a client-side block.
import {
  TIER,
  Q7_CONDITIONS,
  EATING_DISORDER_TYPES,
  Q10_MEDICATIONS,
  BMI_THRESHOLDS,
  CONTINUING_TREATMENT_BMI_FLOOR,
  ethnicityBucket,
} from "../data/eligibility-rules";

// Returns { reason } if formData already contains an absolute (RED) contraindication,
// otherwise null. Safe to call after every step's submit — only looks at whichever
// fields have been filled in so far.
export function checkImmediateRed(formData) {
  if (formData.ageEligible === "No") {
    return { reason: "Outside the 18–84 eligible age range." };
  }

  const q7 = formData.q7Conditions || [];
  for (const condition of Q7_CONDITIONS) {
    if (condition.tier === TIER.RED && q7.includes(condition.key)) {
      return { reason: condition.label };
    }
  }

  if (q7.includes("eatingDisorder")) {
    const type = formData.eatingDisorderType;
    const match = EATING_DISORDER_TYPES.find((t) => t.key === type);
    if (match && match.tier === TIER.RED) {
      return { reason: `Eating disorder — ${match.label}` };
    }
  }

  if (formData.allergyAnswer === "yes") {
    return { reason: "Allergic to a required medication class." };
  }

  if (formData.pregnancyStatus === "Yes") {
    return { reason: "Currently pregnant, breastfeeding, or planning a pregnancy within 2 months." };
  }

  if (formData.pregnancyNoticeResponse === "I don't wish to continue with GLP1 prescription") {
    return { reason: "Declined to confirm contraception/pregnancy-planning acknowledgement." };
  }

  const bmiCheck = checkBmiEligibility(formData);
  if (bmiCheck && !bmiCheck.eligible) {
    return { reason: bmiCheck.reason };
  }

  return null;
}

// Only meaningful once ethnicity (Q6) and comorbidities (Q8) are both known — call
// this after Q8 is submitted, not from the BMI step itself.
export function getBmiThreshold(formData) {
  const bucket = ethnicityBucket(formData.ethnicity);
  const hasComorbidity =
    Array.isArray(formData.q8Comorbidities) &&
    formData.q8Comorbidities.length > 0 &&
    !formData.q8Comorbidities.includes("none");
  const thresholds = BMI_THRESHOLDS[bucket];
  return hasComorbidity ? thresholds.withComorbidity : thresholds.withoutComorbidity;
}

// Returns null if BMI/comorbidities/ethnicity aren't all captured yet (nothing to
// check), otherwise { eligible, reason?, threshold }.
export function checkBmiEligibility(formData) {
  if (formData.bmi == null || !formData.ethnicity || formData.q8Comorbidities == null) {
    return null;
  }

  const threshold = getBmiThreshold(formData);

  if (formData.isContinuingGlp1Treatment && formData.bmi >= CONTINUING_TREATMENT_BMI_FLOOR) {
    return { eligible: true, threshold: CONTINUING_TREATMENT_BMI_FLOOR };
  }

  if (formData.bmi < threshold) {
    return {
      eligible: false,
      threshold,
      reason: `BMI ${formData.bmi.toFixed(1)} is below the qualifying threshold of ${threshold} for your profile.`,
    };
  }

  return { eligible: true, threshold };
}

// Full tier + flag list, computed once at final submission as a safety-net check and
// to attach to the payload sent to the backend for doctor review.
export function computeFinalTier(formData) {
  const flags = [];

  const immediateRed = checkImmediateRed(formData);
  if (immediateRed) {
    flags.push({ tier: TIER.RED, reason: immediateRed.reason });
  }

  const q7 = formData.q7Conditions || [];
  for (const condition of Q7_CONDITIONS) {
    if (condition.tier && condition.tier !== TIER.GREEN && q7.includes(condition.key)) {
      flags.push({ tier: condition.orangeCandidate ? TIER.ORANGE : condition.tier, reason: condition.label });
    }
  }

  const meds = formData.q10Medications || [];
  for (const med of Q10_MEDICATIONS) {
    if (med.tier && med.tier !== TIER.GREEN && meds.includes(med.key)) {
      flags.push({ tier: med.orangeCandidate ? TIER.ORANGE : med.tier, reason: med.label });
    }
  }

  if (formData.otherMedicalConditionsAnswer === "Yes") {
    flags.push({ tier: TIER.AMBER, reason: "Reported other/unlisted health conditions." });
  }

  if (formData.otherMedicationAnswer === "Yes") {
    flags.push({ tier: TIER.AMBER, reason: "Reported other/unlisted current medication." });
  }

  if (formData.hasOtherAllergies) {
    flags.push({ tier: TIER.AMBER, reason: "Reported other (non-GLP1) allergies." });
  }

  if (formData.lastDoseGap === "gt28") {
    flags.push({
      tier: TIER.ORANGE,
      reason: "More than 28 days since last GLP-1 dose — requires forced dose reset / re-titration.",
    });
  } else if (formData.lastDoseGap === "d15to28") {
    flags.push({ tier: TIER.AMBER, reason: "15–28 days since last GLP-1 dose." });
  }

  if (formData.sideEffectSeverity >= 3) {
    flags.push({
      tier: formData.sideEffectSeverity === 3 ? TIER.ORANGE : TIER.AMBER,
      reason: `Reported side effects rated severity ${formData.sideEffectSeverity}/5.`,
    });
  }

  const highestTier = flags.some((f) => f.tier === TIER.RED)
    ? TIER.RED
    : flags.some((f) => f.tier === TIER.ORANGE)
    ? TIER.ORANGE
    : flags.some((f) => f.tier === TIER.AMBER)
    ? TIER.AMBER
    : TIER.GREEN;

  return { tier: highestTier, flags };
}
